/**
 * Coda telemetria con persistenza + retry — redesign-v2/03-player.
 *
 * Comportamento (decisione documentata in docs/redesign/fase-3-player.md §1):
 *  - Ogni evento si tenta di mandare subito tramite sendTelemetry().
 *  - Se fallisce (rete giù, server giù, 5xx), l'evento finisce in una coda
 *    persistente in localStorage (chiave `signedge.telemetry.queue`).
 *  - La coda viene drenata a ogni tick di heartbeat e quando il browser
 *    torna online (event `online`).
 *  - Cap della coda: 100 eventi più recenti. Gli eventi più vecchi vengono
 *    droppati con un warn. Niente accumulo infinito su device dimenticati.
 *  - Ogni evento porta con sé il timestamp di emissione lato device, così
 *    quando il server lo riceve sa quando è successo davvero.
 *  - Niente lib esterne. Vanilla JS, ~80 righe.
 */
import { sendTelemetry } from './telemetry.js';

const STORAGE_KEY = 'signedge.telemetry.queue';
const MAX_QUEUE = 100;

/** @typedef {{ deviceId: string, body: Record<string, unknown>, emittedAt: string }} QueuedEvent */

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // storage pieno o non disponibile: niente di catastrofico, perdiamo la coda
  }
}

function pushToQueue(evt) {
  const queue = readQueue();
  queue.push(evt);
  while (queue.length > MAX_QUEUE) queue.shift();
  writeQueue(queue);
}

/**
 * Invia un evento di telemetria. Se fallisce, finisce in coda persistente.
 *
 * @param {string} deviceId
 * @param {Record<string, unknown>} body — eventType + payload
 */
export async function enqueueTelemetry(deviceId, body) {
  const evt = {
    deviceId,
    body: { ...body, emittedAt: new Date().toISOString() },
    emittedAt: new Date().toISOString(),
  };
  if (!navigator.onLine) {
    pushToQueue(evt);
    return;
  }
  try {
    await sendTelemetry(deviceId, evt.body);
  } catch {
    pushToQueue(evt);
  }
}

/**
 * Drena la coda, mandando un evento alla volta. Si ferma al primo errore:
 * se è andato il primo, va il secondo; se il primo fallisce, ci riproviamo
 * al prossimo drain (preserva l'ordine).
 *
 * @returns {Promise<{ sent: number, remaining: number }>}
 */
export async function drainTelemetryQueue() {
  if (!navigator.onLine) return { sent: 0, remaining: readQueue().length };
  let sent = 0;
  while (true) {
    const queue = readQueue();
    if (queue.length === 0) break;
    const next = queue[0];
    try {
      await sendTelemetry(next.deviceId, next.body);
      // Pop only on success — keep order, don't lose events on partial drain
      queue.shift();
      writeQueue(queue);
      sent += 1;
    } catch {
      break;
    }
  }
  return { sent, remaining: readQueue().length };
}

/** Numero di eventi attualmente in coda (utile per dashboard/log). */
export function getQueueDepth() {
  return readQueue().length;
}

/** Listener: prova a drenare appena la rete torna. */
let _onlineListenerAttached = false;
export function attachOnlineDrainListener() {
  if (_onlineListenerAttached) return;
  _onlineListenerAttached = true;
  window.addEventListener('online', () => {
    drainTelemetryQueue().catch(() => undefined);
  });
}
